import { PWBWorker } from 'promise-worker-bi';

// optional chaining is not supported here
/* eslint-disable @typescript-eslint/prefer-optional-chain */

const promiseWorker = new PWBWorker();

function sumArray(arr) {
  // Normally I'd use .reduce to calculate a sum like this, but for loops are
  // actually faster, and performance matters here
  let sum = 0;
  for (let i = 0; i < arr.length; i++) {
    sum = sum + arr[i];
  }
  return sum;
}

function sumGroups(data, ranges) {
  let dataCopy = [...data];

  let groups = [];
  for (let i = 0; i < ranges.length; i++) {
    let length = ranges[i];
    groups.push(sumArray(dataCopy.splice(0, length)));
  }
  return groups;
}

function groupAndSumMetricData(data, measures, rangeLengths) {
  let groupedData = {};
  for (let i = 0; i < measures.length; i++) {
    let measure = measures[i];
    let values = data[measure];
    if (values == null) {
      continue;
    }
    groupedData[measure] = sumGroups(values, rangeLengths);
  }
  return groupedData;
}

function calculateRatios(numerators, denominators) {
  if (numerators == null || denominators == null) {
    return [];
  }

  // Assume that numerators line up with denominators from beginning. If
  // different lengths, slice from the start.
  if (numerators.length < denominators.length) {
    denominators = denominators.slice(0, numerators.length - 1);
  }

  let ratios = [];
  for (let i = 0; i < denominators.length; i++) {
    let denominator = denominators[i];
    if (denominator == null || denominator === 0) {
      ratios.push(null);
      continue;
    }

    ratios.push(numerators[i] / denominator);
  }
  return ratios;
}

function calculateRates(
  data,
  measures,
  measureType,
  selectedChannels,
  findDenominator,
  perChannelMetrics,
  metricsPerActionType,
) {
  let ratesData = {};
  for (let i = 0; i < measures.length; i++) {
    let measure = measures[i];

    if (measureType === 'delivery_metrics') {
      let denominatorSeries = selectedChannels.reduce((acc, channel) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!metricsPerActionType[`${channel}_action`].includes(measure)) {
          return acc;
        }

        let denominator = findDenominator(measure, 'delivery_metrics', channel);
        if (denominator == null) {
          return acc;
        }

        return perChannelMetrics[channel][denominator].map((v, i) => {
          let currentValue = acc[i];

          if (currentValue == null) {
            return v;
          }

          if (v == null) {
            return currentValue;
          }

          return v + currentValue;
        });
      }, new Array(data[measure].length).fill(null));

      ratesData[`${measure}_rates`] = calculateRatios(data[measure], denominatorSeries);
    } else if (measureType === 'journey_metrics') {
      let denominator = findDenominator(measure, measureType);

      if (denominator == null) {
        continue;
      }

      ratesData[`${measure}_rates`] = calculateRatios(data[measure], data[denominator]);
    }
  }
  return ratesData;
}

export default class TimeGroupAnalysisMetricsWorker {
  constructor() {
    this.findDenominator = this.findDenominator.bind(this);
  }

  findDenominator(measure, measureType, channel) {
    if (measureType === 'delivery_metrics') {
      let measureDefinition = this.deliveryMetricsDenominators[measure];

      if (
        measureDefinition != null &&
        measureDefinition.actionTypeDenominators != null &&
        measureDefinition.actionTypeDenominators[`${channel}_action`] != null
      ) {
        return measureDefinition.actionTypeDenominators[`${channel}_action`];
      }

      return measureDefinition.denominator;
    }

    if (measureType === 'journey_metrics') {
      return this.journeyMetricsDenominators[measure];
    }

    return null;
  }

  process(
    metrics,
    grouping,
    rangeLengths,
    selectedChannels,
    {
      DELIVERY_METRICS_MEASURES,
      JOURNEY_METRICS_MEASURES,
      DELIVERY_METRICS_MEASURE_DENOMINATORS,
      JOURNEY_METRICS_MEASURE_DENOMINATORS,
      METRICS_PER_ACTION_TYPE,
    },
  ) {
    if (grouping === 'daily') {
      return metrics;
    }

    this.deliveryMetricsDenominators = DELIVERY_METRICS_MEASURE_DENOMINATORS;
    this.journeyMetricsDenominators = JOURNEY_METRICS_MEASURE_DENOMINATORS;

    return metrics.map((item) => {
      let summedData = {
        delivery_metrics: { ...item.data.delivery_metrics },
        journey_metrics: { ...item.data.journey_metrics },
        per_channel_metrics: { ...item.data.per_channel_metrics },
      };

      // First pass to group and sum by week/month
      summedData.delivery_metrics = {
        ...summedData.delivery_metrics,
        ...groupAndSumMetricData(item.data.delivery_metrics, DELIVERY_METRICS_MEASURES, rangeLengths),
      };
      summedData.journey_metrics = {
        ...summedData.journey_metrics,
        ...groupAndSumMetricData(
          summedData.journey_metrics,
          [...JOURNEY_METRICS_MEASURES, 'in_workflow'],
          rangeLengths,
        ),
      };
      Object.keys(item.data.per_channel_metrics).forEach((channel) => {
        summedData.per_channel_metrics[channel] = groupAndSumMetricData(
          summedData.per_channel_metrics[channel],
          DELIVERY_METRICS_MEASURES,
          rangeLengths,
        );
      });

      // Second pass to calculate grouped rates
      summedData.delivery_metrics = {
        ...summedData.delivery_metrics,
        ...calculateRates(
          summedData.delivery_metrics,
          DELIVERY_METRICS_MEASURES,
          'delivery_metrics',
          selectedChannels,
          this.findDenominator,
          summedData.per_channel_metrics,
          METRICS_PER_ACTION_TYPE,
        ),
      };
      summedData.journey_metrics = {
        ...summedData.journey_metrics,
        ...calculateRates(
          summedData.journey_metrics,
          [...JOURNEY_METRICS_MEASURES, 'in_workflow'],
          'journey_metrics',
          selectedChannels,
          this.findDenominator,
        ),
      };
      Object.keys(item.data.per_channel_metrics).forEach((channel) => {
        summedData.per_channel_metrics[channel] = {
          ...summedData.per_channel_metrics[channel],
          ...calculateRates(
            summedData.per_channel_metrics[channel],
            DELIVERY_METRICS_MEASURES,
            'delivery_metrics',
            [channel],
            this.findDenominator,
            summedData.per_channel_metrics,
            METRICS_PER_ACTION_TYPE,
          ),
        };
      });
      return {
        ...item,
        data: summedData,
      };
    });
  }
}

promiseWorker.register(async (processArgs) => {
  let processWorker = new TimeGroupAnalysisMetricsWorker();

  return processWorker.process(...processArgs);
});
